import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {StateUpdateChatEventNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DataCaptureChatInput, useDataCaptureChatInputControls} from 'llm/data-capture/DataCaptureChatInput';
import {createDataCaptureAssistantChat, runDataCaptureAssistant} from 'llm/data-capture/DataCaptureDAO';
import {DataCaptureEmptyState} from 'llm/data-capture/DataCaptureEmptyState';
import {DataCaptureMessageThread} from 'llm/data-capture/DataCaptureMessageThread';
import {useDataCaptureChatSessionEvents} from 'llm/data-capture/useDataCaptureChatSessionEvents';
import DocumentActions from 'notebook/documents/DocumentActions';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {DataCaptureSideBySideLayout} from './DataCaptureSideBySideLayout';
import {ChatEventNode, DataCaptureAgentState} from './DataCaptureTypes';

export interface DataCaptureChatProps {
  chatSessionId: string | null;
  onCreateChat: (chatSessionId: string) => void;
  onRequestNewChat: () => void;
}

function getLatestState(events: Array<ChatEventNode>): DataCaptureAgentState | null {
  const stateUpdateEvent = events.findLast(
    (event): event is StateUpdateChatEventNode => event.__typename === 'StateUpdateChatEventNode'
  );
  return stateUpdateEvent?.newState.chat_state.application_state ?? null;
}

/**
 * Top-level component for the data capture chat-based experience.
 *
 * This is similar in structure to the deep research UI, but for now there is duplication due to
 * various differences. Some components can likely be consolidated/shared in the future.
 */
export default function DataCaptureChat({
  chatSessionId,
  onCreateChat,
  onRequestNewChat,
}: DataCaptureChatProps): JSX.Element {
  // (TODO): Refactor to use a common hook useDataCaptureChat() or useAIChat()
  const {inputText, setInputText} = useDataCaptureChatInputControls();
  const [isAssistantRunning, setIsAssistantRunning] = useState<boolean>(false);
  const [hadFailure, setHadFailure] = useState<boolean>(false);
  const {events, isLoading, errorMessage, refetch} = useDataCaptureChatSessionEvents(chatSessionId);
  const latestState = getLatestState(events);

  const draftDocument = latestState?.draft_document;
  const shouldShowDraftDocument = draftDocument != null;

  const handleSubmitMessage = useCallback(async () => {
    try {
      const message = inputText;
      setIsAssistantRunning(true);
      setHadFailure(false);
      setInputText('');

      let chatSessionIdToRun = chatSessionId;
      if (!chatSessionIdToRun) {
        // Create a new chat session if one doesn't exist
        const response = await createDataCaptureAssistantChat();
        chatSessionIdToRun = response.chatId;
        onCreateChat(chatSessionIdToRun);
      }
      // Run the data capture assistant. The events will automatically update as this runs.
      await runDataCaptureAssistant(chatSessionIdToRun, message);
    } catch (error) {
      setHadFailure(true);
      throw error;
    } finally {
      setIsAssistantRunning(false);
    }
  }, [chatSessionId, onCreateChat, inputText, setInputText]);

  const refetchCallback = useCallback(() => {
    refetch();
    if (draftDocument != null) {
      DocumentActions.open(draftDocument.document_api_identifier);
    }
  }, [draftDocument, refetch]);

  if (errorMessage != null) {
    return (
      <div className={css(styles.errorBannerContainer)} data-testid="error-banner-container">
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
      </div>
    );
  }

  const chatContent = (
    <div className={css(styles.chatContainer)} data-testid="chat-container">
      <div data-testid="beta-banner">
        <InlineBanner
          bannerType={InlineBannerType.BETA}
          hideIcon={true}
          text={<span>This is a Benchling Intelligence Feature.</span>}
          supportingInfo={<span>This feature is powered by AI, and may make mistakes.</span>}
          action={{actionType: InlineBannerActionType.ACCORDION}}
        />
      </div>
      <div className={css(styles.messageThreadContainer)} data-testid="message-thread-container">
        {(() => {
          if (events.length > 0) {
            return <DataCaptureMessageThread events={events} hadFailure={hadFailure} />;
          } else if (isLoading || isAssistantRunning) {
            // Show a loading state if we haven't loaded the chat yet, or if we just sent a message
            // and nothing has shown up in the chat history yet.
            return <CenteredHorizontalThrobber data-testid="loading-throbber" />;
          } else {
            return <DataCaptureEmptyState />;
          }
        })()}
      </div>
      <div className={css(styles.inputSection)} data-testid="input-section">
        {events.length > 0 && (
          <div className={css(styles.clearChatContainer)} data-testid="clear-chat-container">
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              text="New Chat"
              onClick={onRequestNewChat}
            />
          </div>
        )}
        <DataCaptureChatInput
          inputText={inputText}
          setInputText={setInputText}
          onSubmit={handleSubmitMessage}
          inputDisabled={isAssistantRunning}
        />
      </div>
    </div>
  );

  const content =
    shouldShowDraftDocument && chatSessionId != null ? (
      <DataCaptureSideBySideLayout chatContent={chatContent} draftDocument={draftDocument} />
    ) : (
      chatContent
    );

  return chatSessionId != null ? (
    <RealtimeUpdateSubscriptionWrapper
      apiId={chatSessionId}
      refetch={refetchCallback}
      messageFilter="no_filter"
    >
      {content}
    </RealtimeUpdateSubscriptionWrapper>
  ) : (
    content
  );
}

const styles = StyleSheet.create({
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
  },
  errorBannerContainer: {
    margin: '0 auto',
  },
  messageThreadContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  inputSection: {
    position: 'relative',
  },
  clearChatContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0',
  },
});
